import type { FireAnalyticsEvent } from '../../../common/types';
import type { searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$data } from './__generated__/searchResults_AtlassianSearchPage_SearchResultsBody_connectionData.graphql';

export type ResultEdges =
	searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$data['edges'];
export type ResultFieldDiagnosticSummary = {
	/** Changed field occurrences across sampled commits, not individual Relay writes. */
	fieldChangeCounts: Record<string, number>;
	/** Executions affected by each structural condition; counts may overlap. No IDs are emitted. */
	resultSetChangeCounts: {
		resultCount: number;
		sampledIdsOrOrder: number;
		invalidSampledIds: number;
		baselineUnavailable: number;
	};
	sampledSuppressedExecutions: number;
	elapsedMs: number;
	/** Comparable result sets with no observed field changes; partial coverage is not equality. */
	noObservedFieldChangesCount: number;
	/** Executions with skipped or incomplete field comparisons, including structural changes. */
	truncatedComparisonCount: number;
};
type Observation = { edges: ResultEdges; resetBaseline: boolean; now: number };
type Diagnostics = {
	observe: (observation: Observation) => ResultFieldDiagnosticSummary | undefined;
	reset: () => void;
};
type Scalar = string | null | undefined;
type Snapshot = {
	resultCount: number;
	ids: (string | undefined)[];
	fields: Record<string, string | undefined>[];
	incomplete: boolean;
};
const SCALAR_FIELDS = [
	'__typename',
	'description',
	'datasourceId',
	'providerId',
	'integrationId',
	'connectorType',
	'connectorId',
	'connectorProduct',
	'ownerId',
	'url',
	'type',
] as const;
// Shared across collectors, queries and component remounts for this page's module instance.
const sharedBudget = { remainingReports: 2 };

const snapshot = (edges: ResultEdges): Snapshot => {
	let incomplete = edges.length > 50;
	const ids: Snapshot['ids'] = [];
	const fields = edges.slice(0, 50).map(({ node }) => {
		const id = node?.id && node.id.length <= 4096 ? node.id : undefined;
		ids.push(id);
		const encoded: Record<string, string | undefined> = {};
		if (!node || !id) {
			incomplete = true;
			return encoded;
		}
		// Fixed selected primitives only: never traverse or retain Relay fragment/owner objects.
		const selected: Record<string, readonly Scalar[] | undefined> = {};
		for (const field of SCALAR_FIELDS) {
			selected[field] = [node[field]];
		}
		selected.issue = node.issue ? ['present', node.issue.webUrl] : [node.issue];
		selected.repository = node.repository ? ['present', node.repository.scm] : [node.repository];
		selected.socialSignal = node.socialSignal
			? ['present', node.socialSignal.signal]
			: [node.socialSignal];
		selected.linkedResults = node.linkedResults
			? node.linkedResults.length > 20
				? undefined
				: node.linkedResults.flatMap(({ id, category, connectorType, datasourceId }) => [
						id,
						category,
						connectorType,
						datasourceId,
					])
			: [node.linkedResults];
		selected.linkedEntities = node.linkedEntities
			? node.linkedEntities.length > 20
				? undefined
				: node.linkedEntities.map(({ id }) => id)
			: [node.linkedEntities];
		for (const [field, values] of Object.entries(selected)) {
			if (!values || values.some((value) => typeof value === 'string' && value.length > 4096)) {
				incomplete = true;
				continue;
			}
			// An array marker preserves missing versus null; source values are primitives only.
			encoded[field] = JSON.stringify(values.map((value) => (value === undefined ? [] : value)));
		}
		return encoded;
	});
	return { resultCount: edges.length, ids, fields, incomplete };
};

/** Counts every suppressed execution; compares fields only for stable sampled result identities. */
export const createResultFieldDiagnostics = (
	budget: { remainingReports: number } = sharedBudget,
): Diagnostics => {
	let baseline: Snapshot | undefined;
	let summary: ResultFieldDiagnosticSummary | undefined;
	let windowStartedAt = 0;
	const reset = () => {
		baseline = undefined;
		summary = undefined;
	};
	const observe = ({
		edges,
		resetBaseline,
		now,
	}: Observation): ResultFieldDiagnosticSummary | undefined => {
		if (budget.remainingReports <= 0) {
			reset();
			return;
		}
		const previous = baseline;
		const current = snapshot(edges);
		baseline = current;
		// Only a genuine analytics reporting transition resets the suppression window.
		// Structural churn is itself evidence: retain its count while refreshing the baseline.
		if (resetBaseline) {
			summary = undefined;
			return;
		}
		if (!summary || now - windowStartedAt > 30_000 || now < windowStartedAt) {
			windowStartedAt = now;
			summary = {
				fieldChangeCounts: {},
				resultSetChangeCounts: {
					resultCount: 0,
					sampledIdsOrOrder: 0,
					invalidSampledIds: 0,
					baselineUnavailable: 0,
				},
				sampledSuppressedExecutions: 0,
				elapsedMs: 0,
				noObservedFieldChangesCount: 0,
				truncatedComparisonCount: 0,
			};
		}
		const currentSummary = summary;
		currentSummary.sampledSuppressedExecutions += 1;
		const resultCountChanged =
			previous !== undefined && previous.resultCount !== current.resultCount;
		const sampledIdsOrOrderChanged =
			previous !== undefined &&
			(previous.ids.length !== current.ids.length ||
				current.ids.some((id, index) => id !== previous.ids[index]));
		const invalidSampledIds =
			current.ids.some((id) => !id) || (previous?.ids.some((id) => !id) ?? false);
		currentSummary.resultSetChangeCounts.resultCount += Number(resultCountChanged);
		currentSummary.resultSetChangeCounts.sampledIdsOrOrder += Number(sampledIdsOrOrderChanged);
		currentSummary.resultSetChangeCounts.invalidSampledIds += Number(invalidSampledIds);
		currentSummary.resultSetChangeCounts.baselineUnavailable += Number(!previous);

		if (previous && !resultCountChanged && !sampledIdsOrOrderChanged && !invalidSampledIds) {
			let changed = false;
			current.fields.forEach((fields, index) => {
				for (const [field, value] of Object.entries(fields)) {
					const before = previous.fields[index][field];
					if (value !== undefined && before !== undefined && value !== before) {
						currentSummary.fieldChangeCounts[field] =
							(currentSummary.fieldChangeCounts[field] ?? 0) + 1;
						changed = true;
					}
				}
			});
			currentSummary.noObservedFieldChangesCount += Number(!changed);
			currentSummary.truncatedComparisonCount += Number(previous.incomplete || current.incomplete);
		} else {
			// Comparing different results by position would mislabel replacements as field writes.
			currentSummary.truncatedComparisonCount += 1;
		}
		if (currentSummary.sampledSuppressedExecutions < 10) {
			return;
		}
		// Reserve before returning so simultaneous collectors cannot exceed the shared cap.
		budget.remainingReports -= 1;
		const report = { ...currentSummary, elapsedMs: now - windowStartedAt };
		summary = undefined;
		if (budget.remainingReports <= 0) {
			reset();
		}
		return report;
	};
	return { observe, reset };
};

export const onResultFieldChangesObserved = (
	summary: ResultFieldDiagnosticSummary,
): FireAnalyticsEvent => ({
	eventType: 'operational',
	actionSubject: 'searchPage',
	action: 'resultFieldChangesObserved',
	attributes: { additionalAttributes: { diagnosticVersion: 2, ...summary } },
});
